import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Phone, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import QuoteWizard from "../components/QuoteWizard";
import { CTA, Reviews } from "../components/sections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, track } from "../lib/api";
import { TOW_IMG, OG_IMG } from "../data/site";

export default function ServicePage({ slug }) {
  const [svc, setSvc] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSvc(null); setNotFound(false);
    api.get(`/content/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setNotFound(true));
    api.get("/content/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { if (notFound) navigate("/404", { replace: true }); }, [notFound, navigate]);

  if (!svc) return <div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-brand-navy" /></div>;

  const seo = svc.seo || {};
  const jsonLd = { "@context": "https://schema.org", "@type": "Service", name: svc.title, provider: { "@type": "AutoDealer", name: "Car To Cash" }, areaServed: "Melbourne & regional Victoria", description: seo.meta_description };
  const faqLd = (svc.faqs && svc.faqs.length) ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: svc.faqs.map((f) => ({ "@type": "Question", name: f[0], acceptedAnswer: { "@type": "Answer", text: f[1] } })) } : null;

  return (
    <>
      <Seo title={seo.meta_title || `${svc.title} | Car To Cash`} description={seo.meta_description || svc.subtitle} canonical={seo.canonical || `https://cartocash.com.au/${slug}`} image={OG_IMG} jsonLd={faqLd || jsonLd} />

      <section className="noise-dark text-white" data-testid="service-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-gold">{svc.nav_label}</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black leading-tight">{svc.title}</h1>
            <p className="mt-4 text-lg text-white/80 max-w-lg">{svc.subtitle}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="tel:0431206206" onClick={() => track("phone_tap", { where: "service" })} className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-6 py-3.5 font-extrabold text-brand-navy hover:bg-brand-goldDark transition-colors" data-testid="service-call-btn"><Phone className="h-4 w-4" /> 0431 206 206</a>
              <a href="#quote" className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-6 py-3.5 font-bold text-white hover:bg-white/10 transition-colors">Get a price <ArrowRight className="h-4 w-4" /></a>
            </div>
          </div>
          <div id="quote" className="scroll-mt-24"><QuoteWizard /></div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-16">
        <p className="text-xl leading-relaxed text-brand-navy font-medium">{svc.intro}</p>
        <div className="mt-6 space-y-4 text-brand-muted leading-relaxed">
          {(svc.body || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
        </div>
        <div className="mt-10 grid gap-3 sm:grid-cols-2">
          {(svc.bullets || []).map((b) => (
            <div key={b} className="flex items-center gap-3 rounded-xl border border-brand-line bg-brand-surface px-4 py-3">
              <Check className="h-5 w-5 text-brand-green shrink-0" strokeWidth={3} /> <span className="font-semibold text-brand-navy">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {svc.faqs && svc.faqs.length > 0 && (
        <section className="mx-auto max-w-4xl px-4 sm:px-6 pb-8" data-testid="service-faq">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-navy">Common questions</h2>
          <Accordion type="single" collapsible className="mt-6 space-y-3">
            {svc.faqs.map((f, i) => (
              <AccordionItem key={f[0]} value={`f${i}`} className="rounded-2xl border border-brand-line bg-white px-5" data-testid={`service-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-bold text-brand-navy hover:no-underline">{f[0]}</AccordionTrigger>
                <AccordionContent className="text-brand-muted leading-relaxed">{f[1]}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center rounded-3xl overflow-hidden border border-brand-line bg-white">
          <div className="p-8 sm:p-12">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-navy">Free removal included</h2>
            <p className="mt-3 text-brand-muted">Every car we buy comes with free removal across Melbourne and regional Victoria — driveable or not.</p>
            <Link to="/car-removals" className="mt-5 inline-flex items-center gap-2 font-bold text-brand-navy hover:text-brand-gold transition-colors">Learn more <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="h-56 lg:h-full min-h-[240px] order-first lg:order-last"><img src={TOW_IMG} alt="Free car removal tow truck" className="h-full w-full object-cover" /></div>
        </div>
      </section>

      <Reviews testimonials={testimonials} />
      <CTA />
    </>
  );
}
